/*
 * Copyright 2018-2024 CommScope, Inc., All rights reserved.
 *
 * This program is confidential and proprietary to CommScope, Inc. (CommScope), and
 * may not be copied, reproduced, modified, disclosed to others, published or used, in
 * whole or in part, without the express prior written permission of CommScope.
 */

import { MuiThemeProvider, createMuiTheme } from '@material-ui/core';
import {
  BlockContainer,
  NoDataToShow,
  TitleBar
} from 'app/components/elements';
import { isSpecificNodeType } from 'app/components/layout/components/sidebar/utils';
import {
  ExportCsvIcon,
  FaIcon,
  LoadingIconPlaceholder,
  StyledExportButton
} from 'app/components/icons';
import { StatefulTable } from 'app/components/tables';
import { FailedFetchStateHandler } from 'app/components/utility';
import {
  dateRangeFilterLabelSelector,
  vertSSIDFilterSelector
} from 'app/redux/filters';
import { selectedPathSelector } from 'app/redux/hierarchy';
import {
  fetchIncidentDetails,
  incidentDetailsFetchStateSelector,
  incidentSummarySelector
} from 'app/redux/incidents';
import { fetchStatePropTypes } from 'app/redux/utils';
import { scrollToBottom } from 'app/utils';
import classNames from 'classnames';
import { Parser } from 'json2csv';
import { findIndex, isArray, isEmpty, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { IncidentActivityTable } from './';
import {
  customMuiTableTheme,
  customTableOptions,
  incidentSummaryTableColumns,
  incidentSummaryTransform
} from './utils';

class IncidentTableSummary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      caseNumber: '',
      incidentId: '',
      visibleColumns: this.getDefaultVisibleColumns(),
      dynamicColumns: this.getDynamicColumns(),
      searchText: ''
    };
  }

  componentDidMount = () => {
    // Setting flag to indicate component is mounted
    this._isMounted = true;
    this.getDynamicColumns();
  };

  componentWillUnmount = () => {
    // Unsetting flag when component is unmounted
    this._isMounted = false;
  };

  componentDidUpdate = prevProps => {
    const { dateRangeFilterLabel, selectedPath, vertSSIDFilter } = this.props;
    const {
      dateRangeFilterLabel: prevDateRange,
      selectedPath: prevSelectedPath,
      vertSSIDFilter: prevVertSSIDFilter
    } = prevProps;

    const pathChanged =
      prevSelectedPath && prevSelectedPath.id !== selectedPath.id;
    const dateRangeChanged = prevDateRange !== dateRangeFilterLabel;
    const vertSSIDFilterChanged = !isEqual(prevVertSSIDFilter, vertSSIDFilter);

    if (pathChanged || dateRangeChanged || vertSSIDFilterChanged) {
      this.setState({ caseNumber: '', incidentId: '' }, () => {
        this.getDynamicColumns();
      });
    }
  };

  toggleIncidentActivityTable = () =>
    this.setState({ caseNumber: '', incidentId: '' });

  handleCSVExport = () => {
    const { incidentSummary = [] } = this.props;
    const { dynamicColumns } = this.state;

    const wrapText = text => {
      if (text === null || text === undefined) {
        return '';
      }
      const normalizedText = text
        .trim()
        .replace(/"/g, '""')
        .replace(/\n+/g, ' ', '\n');

      return `${normalizedText}`;
    };

    const customHeader = dynamicColumns.map(c => ({
      label: c.label,
      value: row => {
        if (c.name === 'curSumNextAction' || c.name === 'description') {
          return wrapText(row[c.name]);
        } else {
          return wrapText(row[c.name]);
        }
      }
    }));
    const parser = new Parser({ fields: customHeader });
    const csv = parser.parse(incidentSummary);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Incidents.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  handleSearchChange = searchText => {
    this.setState({ searchText });
  };

  renderCustomSearch = (searchText, handleSearch) => (
    <div className="d-flex justify-content-start align-items-center">
      <FaIcon icon="search" classes={['mr-2']} />
      <label className="sr-only">Search</label>
      <input
        className="p-1"
        id="incident-summary-search"
        onChange={e => {
          handleSearch(e.target.value);
          this.handleSearchChange(e.target.value);
        }}
        placeholder="Search incidents..."
        style={{ width: 250 }}
        type="text"
        value={searchText || ''}
      />
    </div>
  );

  handleTableRowClick = (rowData, columnIndex) => {
    const newSelectedCase = rowData[columnIndex];
    const incidentIdColumnIndex = findIndex(incidentSummaryTableColumns, [
      'name',
      'incidentid'
    ]);
    const incidentId = rowData[incidentIdColumnIndex];
    if (newSelectedCase === this.state.caseNumber) {
      this.setState({ caseNumber: '', incidentId: '' });
    } else {
      this.setState(
        { caseNumber: newSelectedCase, incidentId },
        scrollToBottom
      );
    }
  };

  setRowProps = (row, columnIndex) => {
    const { caseNumber } = this.state;
    const newSelectedCase = row[columnIndex];
    return {
      className:
        caseNumber !== '' &&
        classNames(
          newSelectedCase === caseNumber ? 'row-selected' : 'row-unselected'
        )
    };
  };

  getDynamicColumns = () => {
    const { selectedPath } = this.props;
    const isPropertyLevel = isSpecificNodeType(selectedPath, 'zonename');
    const columns = incidentSummaryTableColumns.map(column => {
      if (
        column.name === 'datetimeopened' ||
        column.name === 'issuestart' ||
        column.name === 'issuereported' ||
        column.name === 'issueresolved' ||
        column.name === 'datatimelastmodified' ||
        column.name === 'recoverydatetime' ||
        column.name === 'resolutiondate'
      ) {
        return {
          ...column,
          label: isPropertyLevel ? column.label : `${column.label} (UTC)`
        };
      }
      return column;
    });
    if (this._isMounted) {
      this.setState({ dynamicColumns: columns });
    }
  };

  getDefaultVisibleColumns = () => {
    return incidentSummaryTableColumns
      .filter(column => !column.options || column.options.display !== false)
      .map(column => column.name);
  };

  handleColumnViewChange = (changedColumn, action) => {
    this.setState(prevState => {
      let updatedVisibleColumns = [...prevState.visibleColumns];
      if (action === 'add') {
        updatedVisibleColumns.push(changedColumn);
      } else {
        updatedVisibleColumns = updatedVisibleColumns.filter(
          column => column !== changedColumn
        );
      }
      return { visibleColumns: updatedVisibleColumns };
    });
  };

  render = () => {
    const {
      fetchIncidentDetails,
      incidentSummary = [],
      fetchState
    } = this.props;
    const {
      caseNumber,
      incidentId,
      visibleColumns,
      dynamicColumns,
      searchText
    } = this.state;

    const { pending, complete, failed } = fetchState;
    const hasData = isArray(incidentSummary) && !isEmpty(incidentSummary);
    const noDataToShow = !pending && complete && !hasData;
    const transformedIncidentSummary = incidentSummaryTransform(
      incidentSummary
    );

    const columns = dynamicColumns
      ? dynamicColumns.map(column => ({
        ...column,
        options: {
          ...(column.options || {}),
          display: visibleColumns.includes(column.name)
        }
      }))
      : [];

    const incidentSummaryOptions = {
      ...customTableOptions,
      customSearchRender: (searchText, handleSearch) => this.renderCustomSearch(searchText, handleSearch),
      onRowClick: rowData => this.handleTableRowClick(rowData, columnIndex),
      setRowProps: row => this.setRowProps(row, columnIndex),
      onColumnViewChange: this.handleColumnViewChange,
      fixedSelectColumn: {
        columnIndex: 0,
        align: 'right'
      },
      columnView: visibleColumns,
      searchText: searchText
    };

    const columnIndex = findIndex(incidentSummaryTableColumns, [
      'name',
      'casenumber'
    ]);

    return (
      <Fragment>
        <BlockContainer classes={['mb-4', 'h-auto']}>
          <TitleBar
            leftChildren="Incidents Summary"
            rightChildren={
              complete &&
              hasData && (
                <StyledExportButton
                  title="Generate CSV"
                  onClick={this.handleCSVExport}
                >
                  <ExportCsvIcon />
                </StyledExportButton>
              )
            }
          />
          <FailedFetchStateHandler
            fetchState={fetchState}
            retry={fetchIncidentDetails}
          >
            {pending ? (
              <LoadingIconPlaceholder position="inherit" />
            ) : noDataToShow || failed ? (
              <NoDataToShow
                message={
                  failed
                    ? 'There has been a problem fetching the incidents summary'
                    : 'No incidents available'
                }
                style={{ background: 'none', position: 'relative' }}
              />
            ) : (
              complete &&
              hasData && (
                <MuiThemeProvider theme={createMuiTheme(customMuiTableTheme)}>
                  <StatefulTable
                    tableKey="incident-summary-table"
                    data={transformedIncidentSummary}
                    columns={columns}
                    options={incidentSummaryOptions}
                  />
                </MuiThemeProvider>
              )
            )}
          </FailedFetchStateHandler>
        </BlockContainer>
        {caseNumber !== '' && (
          <IncidentActivityTable
            caseNumber={caseNumber}
            incidentId={incidentId}
            toggleIncidentActivityTable={this.toggleIncidentActivityTable}
          />
        )}
      </Fragment>
    );
  };
}

IncidentTableSummary.propTypes = {
  dateRangeFilterLabel: PropTypes.string,
  selectedPath: PropTypes.object,
  vertSSIDFilter: PropTypes.object,
  fetchIncidentDetails: PropTypes.func,
  incidentSummary: PropTypes.array,
  fetchState: fetchStatePropTypes
};

const mapStateToProps = createSelector(
  vertSSIDFilterSelector,
  dateRangeFilterLabelSelector,
  selectedPathSelector,
  incidentSummarySelector,
  incidentDetailsFetchStateSelector,
  (
    vertSSIDFilter,
    dateRangeFilterLabel,
    selectedPath,
    incidentSummary,
    fetchState
  ) => ({
    vertSSIDFilter,
    dateRangeFilterLabel,
    selectedPath,
    incidentSummary,
    fetchState
  })
);

const mapDispatchToProps = { fetchIncidentDetails };

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(IncidentTableSummary);
